import React, { FC, useMemo } from "react"

import { Autocomplete, Box, Group, MultiSelect, Popover, Select, useMantineTheme } from "@mantine/core"
import { useDisclosure, useMediaQuery } from "@mantine/hooks"
import isEqual from "lodash.isequal"

import { DeviceFilter, INIT_DEVICES_FILTER } from "../../hooks/useDevicesFilter"
import { useLanguage } from "../../../../contexts/language-context"
import { useDevices, useGroups } from "../../../../contexts/devices-context"
import TransparentButton from "../../../../components/inputs/transparent-button/TransparentButton"
import data from "../services-summary/data"
import FilteringButton from "./FilteringButton"
import DevicesFilterDrawer from "./DevicesFilterDrawer"
import { IconFilter, IconFilterSearch } from "@tabler/icons-react"
import { colorPerStatus } from "../../hooks/useInferStatus"
import DeviceStatusSelectItem from "./DeviceStatusSelectItem"

export type DevicesFilterProps = {
    filter: DeviceFilter
    setNameFilter: (value: string | null) => void
    setGroupFilter: (value: string | null) => void
    setStatusFilter: (value: string | null) => void
    setModulesFilter: (values: string[] | null) => void
    resetFilter: () => void
}

const DevicesFilter: FC<DevicesFilterProps> = ({
    filter,
    setModulesFilter,
    setGroupFilter,
    setStatusFilter,
    setNameFilter,
    resetFilter,
}) => {
    const { data: devices } = useDevices()
    const { data: groups, map } = useGroups()
    const { L } = useLanguage()
    const [opened, { open, close }] = useDisclosure(false)
    const theme = useMantineTheme()
    const mobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`)
    const filterIsSet = useMemo(() => !isEqual(filter, INIT_DEVICES_FILTER), [filter])

    if (mobile)
        return (
            <>
                <TransparentButton
                    variant="outline"
                    rightIcon={filterIsSet ? <IconFilterSearch size={20} /> : <IconFilter size={20} />}
                    onClick={open}
                    data-active={filterIsSet}
                >
                    {L("Filter devices")}
                </TransparentButton>
                <DevicesFilterDrawer
                    filter={filter}
                    setNameFilter={setNameFilter}
                    setGroupFilter={setGroupFilter}
                    setStatusFilter={setStatusFilter}
                    setModulesFilter={setModulesFilter}
                    resetFilter={resetFilter}
                    opened={opened}
                    onClose={close}
                />
            </>
        )
    else
        return (
            <Group>
                <Popover position="bottom-start">
                    <Popover.Target>
                        <Box>
                            <FilteringButton
                                label={L("Name")}
                                filterValue={filter.name}
                                clear={() => setNameFilter(INIT_DEVICES_FILTER.name)}
                            />
                        </Box>
                    </Popover.Target>
                    <Popover.Dropdown p={0}>
                        <Autocomplete
                            data={devices.map(device => ({ value: device.id, label: device.name }))}
                            value={filter.name ?? ""}
                            onChange={setNameFilter}
                            placeholder={L("Type to filter")}
                            dropdownPosition="bottom"
                            autoFocus
                        />
                    </Popover.Dropdown>
                </Popover>
                <Popover position="bottom-start">
                    <Popover.Target>
                        <Box>
                            <FilteringButton
                                label={L("Group")}
                                filterValue={filter.group ? map[filter.group].name : null}
                                clear={() => setGroupFilter(INIT_DEVICES_FILTER.group)}
                            />
                        </Box>
                    </Popover.Target>
                    <Popover.Dropdown p={0}>
                        <Select
                            data={groups.map(group => ({ value: group.id, label: group.name }))}
                            value={filter.group}
                            onChange={setGroupFilter}
                            placeholder={L("Type to filter")}
                            dropdownPosition="bottom"
                            searchable
                            clearable
                            autoFocus
                        />
                    </Popover.Dropdown>
                </Popover>
                <Popover position="bottom-start">
                    <Popover.Target>
                        <Box>
                            <FilteringButton
                                label={L("Status")}
                                filterValue={filter.status}
                                clear={() => setStatusFilter(INIT_DEVICES_FILTER.status)}
                            />
                        </Box>
                    </Popover.Target>
                    <Popover.Dropdown p={0}>
                        <Select
                            data={Object.keys(colorPerStatus)}
                            value={filter.status}
                            onChange={setStatusFilter}
                            itemComponent={DeviceStatusSelectItem}
                            placeholder={L("Type to filter")}
                            dropdownPosition="bottom"
                            searchable
                            clearable
                            autoFocus
                        />
                    </Popover.Dropdown>
                </Popover>
                <Popover position="bottom-start">
                    <Popover.Target>
                        <Box>
                            <FilteringButton
                                label={L("Modules")}
                                filterValue={
                                    filter.modules?.length
                                        ? filter.modules.map(value => data[value as keyof typeof data].getLabel(L))
                                        : null
                                }
                                clear={() => setModulesFilter(INIT_DEVICES_FILTER.modules)}
                            />
                        </Box>
                    </Popover.Target>
                    <Popover.Dropdown p={0}>
                        <MultiSelect
                            data={Object.entries(data).map(([key, module]) => ({
                                value: key,
                                label: module.getLabel(L),
                            }))}
                            value={filter.modules ?? []}
                            onChange={setModulesFilter}
                            placeholder={L("Type to filter")}
                            dropdownPosition="bottom"
                            searchable
                            clearable
                            autoFocus
                        />
                    </Popover.Dropdown>
                </Popover>
            </Group>
        )
}

export default DevicesFilter
